import type { Schemas } from "@softmax/observatory-client/types";
import {
  parseEpisodeSeatResult,
  type EpisodeSeatResult,
} from "../../lib/episodes/episode-results";
import { plainName } from "../../lib/program-names";
import type { ProgramResult } from "./program-records";
function outcomeOf({
  results,
  seat,
}: {
  readonly results: EpisodeSeatResult | null;
  readonly seat: number;
}): ProgramResult["outcome"] {
  const wins = results?.win;
  const won = wins?.[seat];

  if (wins && won === undefined) return null;

  if (won === true) return "won";

  if (wins) return wins.some(Boolean) ? "lost" : "draw";

  const winner = results?.winner;

  if (winner === undefined) return null;

  if (winner < 0) return "draw";

  return winner === seat ? "won" : "lost";
}
/** The program's last few games: its seat's outcome and who else played. */
export function recentResultsOf({
  policyVersionId,
  entries,
  replayHrefOf,
  limit = 3,
}: {
  readonly policyVersionId: string;
  readonly entries: readonly Schemas["EpisodeSummary"][];
  readonly replayHrefOf: (episodeId: string) => string;
  readonly limit?: number;
}): ProgramResult[] {
  return entries.slice(0, limit).flatMap((entry) => {
    const seat = entry.policies.findIndex(
      (p) => p.policy_version_id === policyVersionId,
    );

    if (seat === -1) return [];

    const results = parseEpisodeSeatResult(entry.results);
    const scores = results?.scores;
    const score = scores?.[seat];
    const opponents = [
      ...new Set(
        entry.policies.flatMap((p) =>
          p.policy_version_id === policyVersionId || !p.policy_name
            ? []
            : [
                p.version === null
                  ? plainName(p.policy_name)
                  : `${plainName(p.policy_name)}:v${p.version}`,
              ],
        ),
      ),
    ];
    return [
      {
        episodeId: entry.episode_id,
        at: entry.created_at,
        outcome: outcomeOf({
          results,
          seat,
        }),
        place:
          !scores || score === undefined
            ? null
            : {
                rank: 1 + scores.filter((value) => value > score).length,
                of: scores.length,
              },
        opponents,
        replayHref: entry.replay_url ? replayHrefOf(entry.episode_id) : null,
      },
    ];
  });
}
